import { useState } from 'react';

import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer
} from 'recharts';

import './styles.css';

const CustomDot = ({ 
  cx, 
  cy, 
  payload, 
  dataKey, 
  countKey, 
  name, 
  color, 
  r,
  activePoint,
  setActivePoint 
}) => {
  if (cx === null || cy === null || payload[dataKey] === null) return null;

  const isActive = activePoint?.dataKey === dataKey && activePoint?.name === payload.name;
  const value = payload[dataKey];
  const count = payload[countKey];

  return (
    <circle
      cx={cx}
      cy={cy}
      r={isActive ? r + 2 : r}
      fill={color}
      style={{ cursor: 'pointer' }}
      onMouseEnter={() => setActivePoint({ 
        dataKey, 
        name: payload.name, 
        lineName: name,
        color,
        cx, 
        cy,
        value,
        count
      })}
      onMouseLeave={() => setActivePoint(null)}
    />
  );
};


export default function LineChart({
  data,
  lines,
  title,
  xAxisKey = 'name',
  yAxisDomain = [0, 100],
  yAxisTicks,
  yAxisFormatter,
  tooltipFormatter,
  height = 250,
  strokeWidth = 3,
  dotRadius = 3.11,
  showGrid = false,
  showLegend = true,
  legendAlign = 'left',
  legendVerticalAlign = 'top',
  curveType = 'monotone',
  connectNulls = true,
}) {
  const [activePoint, setActivePoint] = useState(null);

  const handleMouseLeave = () => {
    setActivePoint(null);
  };

  return (
    <div className="line-chart-container">
      {title && <h2 className="line-chart-title">{title}</h2>}
      <div className="line-chart-wrapper" onMouseLeave={handleMouseLeave}>
        <ResponsiveContainer width="100%" height={height}>
          <RechartsLineChart
            data={data}
            margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
          >
            {showGrid && (
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#E5E7EB"
              />
            )}
            <XAxis
              dataKey={xAxisKey}
              axisLine={false}
              tickLine={false}
              dy={10}
              padding={{ left: 20 }}
            />
            <YAxis
              domain={yAxisDomain}
              ticks={yAxisTicks}
              axisLine={false}
              tickLine={false}
              tickFormatter={yAxisFormatter}
              dx={-10}
            />
            {showLegend && (
              <Legend
                verticalAlign={legendVerticalAlign}
                align={legendAlign}
                iconType="circle"
                wrapperStyle={{ paddingBottom: 30 }}
              />
            )}
            {lines.map((line) => (
              <Line
                key={line.dataKey}
                type={curveType}
                dataKey={line.dataKey}
                name={line.name}
                stroke={line.color}
                strokeWidth={strokeWidth}
                connectNulls={connectNulls}
                dot={(props) => (
                  <CustomDot
                    {...props}
                    dataKey={line.dataKey}
                    countKey={line.countKey}
                    name={line.name}
                    color={line.color}
                    r={dotRadius}
                    tooltipFormatter={tooltipFormatter}
                    activePoint={activePoint}
                    setActivePoint={setActivePoint}
                  />
                )}
                activeDot={false}
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
        {activePoint && (
          <div 
            className="line-chart-tooltip"
            style={{
              left: activePoint.cx,
              top: activePoint.cy + 20,
            }}
          >
            <div className="line-chart-tooltip-title">{activePoint.lineName}</div>
            <div className="line-chart-tooltip-row">
              <span className="line-chart-tooltip-label">Accuracy Score</span>
              <span className="line-chart-tooltip-value">
                {tooltipFormatter ? tooltipFormatter(activePoint.value) : `${activePoint.value.toFixed(2)}%`}
              </span>
            </div>
            <div className="line-chart-tooltip-row">
              <span className="line-chart-tooltip-label">Student Count</span>
              <span className="line-chart-tooltip-value">{activePoint.count ?? 0}</span>
            </div>
            <div className="line-chart-tooltip-arrow" />
          </div>
        )}
      </div>
    </div>
  );
}